import { useQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import React from 'react';
import EmptyState from '../../components/EmptyState';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import { gql } from '../../graphql/generated/gql';
import type { UserPostsFilter } from '../../graphql/generated/graphql';
import { UserPostsOrder } from '../../graphql/generated/graphql';
import PostRow from '../../post/PostRow';
import ConnectionHelper from '../../utils/graphql/ConnectionHelper';

const emptyProps = (filter: UserPostsFilter, name: string) =>
  ({
    PUBLISHED: {
      title: 'No published posts here…',
      message: `${name} doesn’t have any published posts.`,
    },
    CONTRIBUTED: {
      title: 'No contributed posts here…',
      message: `${name} doesn’t have any contributed posts.`,
    },
    VERIFIED: {
      title: 'No verified posts here…',
      message: `${name} hasn’t have any verified posts.`,
    },
    ALL: {
      title: 'No posts here…',
      message: `${name} doesn’t have any posts.`,
    },
    TEMPLATE: {
      title: 'No templates here...',
      message: `${name} doesn't have any templates.`,
    },
  })[filter];

const USER_POSTS_QUERY = gql(/** @complexity 17 */ `
  query UserPosts(
    $id: ID!
    $afterCursor: String
    $filter: UserPostsFilter
    $orderBy: UserPostsOrder
  ) {
    user(id: $id) {
      id
      name
      posts(
        first: 20
        after: $afterCursor
        filter: $filter
        orderBy: $orderBy
      ) {
        edges {
          node {
            views
            updatedAt
            publishedAt
            ...PostRow_post
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

interface UserPostsListProps {
  userId: string;
  filter: UserPostsFilter;
  orderBy: UserPostsOrder;
}

const METADATA_ATTRIBUTE = {
  [UserPostsOrder.Popularity]: 'popularity',
  [UserPostsOrder.RecentlyPublished]: 'publishedAt',
  [UserPostsOrder.RecentlyUpdated]: 'updatedAt',
  [UserPostsOrder.Alphabetical]: 'updatedAt',
} as const;

const UserPostsList = ({ userId, filter, orderBy }: UserPostsListProps) => {
  const result = useQuery(USER_POSTS_QUERY, {
    variables: { id: userId, filter, orderBy },
  });
  const fetchMore = ConnectionHelper.generateFetchMore(
    USER_POSTS_QUERY,
    result,
    'user.posts',
  );
  const { data: { user } = {} } = result;

  const posts = _.compact(user?.posts?.edges?.map((edge) => edge?.node));

  if (!user || !posts) return null;

  if (posts.length === 0) {
    return <EmptyState imageName="posts" {...emptyProps(filter, user?.name)} />;
  }

  return (
    <>
      {posts.map((post) => (
        <PostRow
          key={post.id}
          post={post}
          meta={
            <PostRow.Meta
              attribute={METADATA_ATTRIBUTE[orderBy]}
              values={{
                publishedAt: post.publishedAt,
                updatedAt: post.updatedAt,
                popularity: post.views,
              }}
            />
          }
        />
      ))}
      <InfiniteScrollWaypoint fetchMore={fetchMore} />
    </>
  );
};

export default UserPostsList;
